import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, timeout } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import ErrorHelper from '../helpers/error-helper';
import { NOT_FOUND } from './request.service';
import { LoadingService } from './loading.service';
import { Router } from '@angular/router';
import { SnackService } from './snack.service';
import { API_URL } from '../../globals/globals';

const DEFAULT_HTTP_TIMEOUT = 180000;

export const DeserializeVoid = () => {};

export type RequestServices = {
  http: HttpClient;
  loadingService: LoadingService;
  router: Router;
  snackService: SnackService;
};
const handleError = (
  requestServices: RequestServices,
  error: HttpErrorResponse
): Observable<never> => {
  requestServices.loadingService.savingFailed();
  console.error(error);
  let errMsg = 'Er is iets misgegaan, probeer later opnieuw.';
  if (error.error instanceof ErrorEvent) {
    console.error('An error occurred:', error.message);
  } else {
    console.error(
      `Backend returned code ${error.status}, ` + `body was: ${error.message}`
    );
    if (ErrorHelper.isConnectionLostError(error)) {
      requestServices.snackService.showWarning('Kon server niet bereiken');
      return throwError(error);
    }
    if (error.status === 404) {
      requestServices.snackService.showWarning('Niets gevonden.');
      return throwError(NOT_FOUND);
    }
    if (error.status && error.status !== 504 && error.status !== 400) {
      if (
        error.error &&
        typeof error.error === 'string' &&
        error.status !== 413
      ) {
        errMsg = `Error: ${error.error}`;
      } else if (error.status === 413) {
        errMsg = 'Request overschreed limiet op grootte';
      } else if (error.status === 422) {
        errMsg = error.error.message;
      } else {
        errMsg = `Error: ${error.message}`;
      }
    }
    if (error.status === 401) {
      localStorage.removeItem('inpress_access_token');
      requestServices.router.navigateByUrl('/login');
    }
  }
  requestServices.snackService.showError(errMsg);
  return throwError(error);
};

export const _get =
  <T, Input>(
    baseUrl: string,
    requestServices: RequestServices
  ): ((
    path: string,
    responseDeserializer: (response: any) => T
  ) => Observable<T>) =>
  (path, responseDeserializer) =>
    requestServices.http.get<T | void>(`${API_URL}${baseUrl}${path}`).pipe(
      timeout(DEFAULT_HTTP_TIMEOUT),
      map(responseDeserializer),
      catchError((e) => handleError(requestServices, e))
    );

export const _post =
  <T, Input>(
    baseUrl: string,
    requestServices: RequestServices
  ): ((
    path: string,
    body: Input,
    responseDeserializer: (response: any) => T
  ) => Observable<T>) =>
  (path, body, responseDeserializer) =>
    requestServices.http
      .post<T | void>(`${API_URL}${baseUrl}${path}`, body)
      .pipe(
        timeout(DEFAULT_HTTP_TIMEOUT),
        map(responseDeserializer),
        catchError((e) => handleError(requestServices, e))
      );

export const _put =
  <T, Input>(
    baseUrl: string,
    requestServices: RequestServices
  ): ((
    path: string,
    body: Input,
    responseDeserializer: (response: any) => T
  ) => Observable<T>) =>
  (path, body, responseDeserializer) =>
    requestServices.http.put<T>(`${API_URL}${baseUrl}${path}`, body).pipe(
      timeout(DEFAULT_HTTP_TIMEOUT),
      map(responseDeserializer),
      catchError((e) => handleError(requestServices, e))
    );

export const _delete =
  <T>(
    baseUrl: string,
    requestServices: RequestServices
  ): ((path: string) => Observable<void>) =>
  (path) =>
    requestServices.http.delete<T | void>(`${API_URL}${baseUrl}${path}`).pipe(
      timeout(DEFAULT_HTTP_TIMEOUT),
      map(DeserializeVoid),
      catchError((e) => handleError(requestServices, e))
    );
